import { Action } from 'history'
import history from 'history/browser'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { ROUTES } from '@constants/index'
import { config } from '@config/config'
import { useAnalytics } from '@hooks/useAnalytics'
import { AppNavigatePartialState, useAppNavigate } from '@hooks/useAppNavigate'
import { useDebouncedCallback } from '@hooks/useDebouncedCallback'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInvokeAction } from '@hooks/useInvokeAction'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { useWebchat } from '@hooks/useWebchat'
import { resetSubmitStatus } from '@redux/slices/claimSubmitSlice'
import { resetInsuranceMemberState } from '@redux/slices/insuranceMemberSlice'
import {
  resetWorkflow,
  showApp,
  setAppVisibleAllowed,
  appBeforeCloseCallbackSelector,
  navigateBackCallbackSelector,
} from '@redux/slices/workflowSlice'
import { logger } from '@utils/logger'
import { clearLocalBasePath, isLocationAllowedToProceed, shouldNavigateToResume } from '@utils/navigationUtil'
import { scrollToDrawerTop } from '@utils/scrollToDrawerTop'
import { useAppDrawerState } from './useAppDrawerState'

type UseAppDrawerResult = {
  onClose: () => void
  onNavigateBack?: () => void
}

export const useAppDrawer = (): UseAppDrawerResult => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useAppNavigate()
  const buildPath = usePathBuilder()
  const { pushOpenEvent } = useAnalytics()
  const { basePath, basePathSearch } = useEnvironment()
  const { isAuthenticated, isSessionReady } = useSessionInfo()
  const { isVisible, appClosing, appVisibleAllowed, activeStep } = useAppDrawerState()
  const onBeforeClose = useSelector(appBeforeCloseCallbackSelector)
  const onNavigateBack = useSelector(navigateBackCallbackSelector)
  const notifyFormAction = useInvokeAction()
  const { invokeWebchat } = useWebchat()

  const locationState: AppNavigatePartialState = location.state
  const basePathWithSearch = `${basePath}${basePathSearch}`

  const handleOpen = useCallback(() => {
    const openDrawer = () => {
      // resume to correct view on page reload
      if (!locationState?.isNavigated && shouldNavigateToResume()) {
        navigate(buildPath(ROUTES.START))
      }

      dispatch(showApp())
      pushOpenEvent()
      invokeWebchat('hide')
    }

    const handleNonAllowedMatch = () => {
      if (appVisibleAllowed) return

      const isMatch = window.location.pathname.includes(ROUTES.BASE)

      // reloaded when drawer was open
      if (isMatch && isLocationAllowedToProceed()) {
        // causes effect to run again and open drawer
        dispatch(setAppVisibleAllowed())
        return
      }

      // reject direct url access or last path since reload was different
      if (isMatch) {
        history.replace(basePathWithSearch)
      }

      clearLocalBasePath()
    }

    // open drawer on location match

    const isMatch = location.pathname.includes(ROUTES.BASE)
    const disableAuthTokenForGuest = config.getFeatures()?.disableAuthTokenForGuest

    if (isMatch && !isVisible && !appClosing) {
      // prevent drawer open if flow restricts guest access
      if (disableAuthTokenForGuest && !isAuthenticated) {
        // do not modify history if session is not initialized, let effect re-run
        if (isSessionReady) {
          history.replace(basePathWithSearch)
        }
        return
      }

      // allowed navigation or page reload while drawer open
      if (appVisibleAllowed || isVisible) {
        openDrawer()
      } else {
        handleNonAllowedMatch()
      }
    }
  }, [
    dispatch,
    navigate,
    buildPath,
    pushOpenEvent,
    invokeWebchat,
    locationState,
    basePathWithSearch,
    location,
    appVisibleAllowed,
    isVisible,
    appClosing,
    isAuthenticated,
    isSessionReady,
  ])

  const debouncedHandler = useDebouncedCallback(handleOpen, 50)

  // open drawer on location match
  useEffect(() => {
    debouncedHandler()
    return debouncedHandler.cancel
  }, [debouncedHandler])

  const resetState = useCallback(() => {
    invokeWebchat('reset')
    dispatch(resetSubmitStatus())
    dispatch(resetInsuranceMemberState())
  }, [invokeWebchat, dispatch])

  const closeDrawer = useCallback(() => {
    dispatch(resetWorkflow())

    // replace our history with base path (e.g. cart or checkout)
    // so that back button will work correctly
    history.replace(basePathWithSearch)
  }, [dispatch, basePathWithSearch])

  /**
   * On back navigation, clean up state if drawer is not closed
   */
  useEffect(() => {
    if (!isVisible) return

    const unlisten = history.listen(({ location, action }) => {
      if (action === Action.Pop && isVisible) {
        // back navigation when drawer still open, need to reset state
        const pathMatch = location.pathname.includes(`${ROUTES.BASE}`)
        if (!pathMatch) {
          closeDrawer()
          resetState()
        }
      }
    })

    return unlisten
  }, [resetState, closeDrawer, isVisible])

  const cleanupOnClose = useCallback(() => {
    resetState()
    clearLocalBasePath()
  }, [resetState])

  useEffect(() => {
    if (isVisible && appClosing) {
      closeDrawer()
      cleanupOnClose()
    }
  }, [closeDrawer, cleanupOnClose, isVisible, appClosing])

  /**
   * Step navigation
   */
  useEffect(() => {
    if (isVisible && !appClosing && activeStep) {
      scrollToDrawerTop()
      notifyFormAction()
      invokeWebchat('hide')
    }
  }, [notifyFormAction, invokeWebchat, activeStep, isVisible, appClosing])

  /**
   * Handle app drawer close button - invokes onBeforeClose to perform optional, view specific
   * cleanup.
   */
  const onClose = useCallback(() => {
    closeDrawer()

    if (!onBeforeClose || typeof onBeforeClose !== 'function') {
      cleanupOnClose()
      return
    }

    onBeforeClose()
      .catch(error => logger.warn('Error on app drawer before close', error))
      .finally(cleanupOnClose)
  }, [closeDrawer, cleanupOnClose, onBeforeClose])

  return {
    onClose,
    onNavigateBack,
  }
}
